// Changes made
// get element by id from CartDrawer to cart-drawer and added section, also changed section.id to section.section

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.section);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    if (window.location.pathname === routes.cart_url) {
      return [
        {
          id: 'cart-drawer',
          section: document.getElementById('CartDrawer').dataset.id,
          selector: '#cart-drawer',
        },
        {
          id: 'main-cart-items',
          section: document.getElementById('main-cart-items').dataset.id,
          selector: '#main-cart-items',
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
        },
        {
          id: 'main-cart-footer',
          section: document.getElementById('main-cart-footer').dataset.id,
          selector: '#main-cart-footer',
        },
      ];
    } else {
      return [
        {
          id: 'cart-drawer',
          section: document.getElementById('CartDrawer').dataset.id,
          selector: '#cart-drawer',
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
        },
      ];
    }
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  constructor() {
    super();
    
    // Add real-time sample validation for cart drawer
    this.addEventListener('input', (event) => {
      if (event.target.matches('.sample-quantity__input')) {
        this.validateSampleQuantityInput(event.target);
      }
    });
    
    // Listen for plus/minus button clicks
    this.addEventListener('click', (event) => {
      if (event.target.matches('.quantity__button')) {
        // Find the associated input
        const quantityInput = event.target.closest('quantity-input')?.querySelector('.sample-quantity__input');
        if (quantityInput) {
          // Use setTimeout to let the button update the value first
          setTimeout(() => {
            this.validateSampleQuantityInput(quantityInput);
          }, 10);
        }
      }
    });
    
    // Initialize button states when cart drawer loads
    setTimeout(() => {
      this.updateSamplePlusButtons();
    }, 100);
  }

  validateSampleQuantityInput(input) {
    const newQuantity = parseInt(input.value) || 0;
    
    // Get max samples from global variable set by cart page, fallback to DOM or default
    const maxSampleTotal = window.maxSamplesSetting || 
                          (document.querySelector(".max-samples [data-max]")?.dataset.max ? 
                           parseInt(document.querySelector(".max-samples [data-max]").dataset.max) : 3);
    
    // Simple immediate validation - don't allow more than max in any single input
    if (newQuantity > maxSampleTotal) {
      input.value = maxSampleTotal;
      return;
    }
    
    // Count existing samples in the DOM (more reliable than async fetch)
    const allSampleInputs = document.querySelectorAll('.sample-quantity__input');
    let totalSamples = 0;
    
    allSampleInputs.forEach(sampleInput => {
      if (sampleInput !== input) { // Don't count the current input
        const qty = parseInt(sampleInput.value) || 0;
        totalSamples += qty;
      }
    });
    
    const newTotal = totalSamples + newQuantity;
    
    if (newTotal > maxSampleTotal) {
      const maxAllowed = Math.max(0, maxSampleTotal - totalSamples);
      input.value = maxAllowed;
    }
    
    // Update button states after validation
    this.updateSamplePlusButtons();
  }

  updateSamplePlusButtons() {
    // Get max samples from global variable set by cart page, fallback to DOM or default
    const maxSampleTotal = window.maxSamplesSetting || 
                          (document.querySelector(".max-samples [data-max]")?.dataset.max ? 
                           parseInt(document.querySelector(".max-samples [data-max]").dataset.max) : 3);
    
    // Count current total samples
    const allSampleInputs = document.querySelectorAll('.sample-quantity__input');
    let totalSamples = 0;
    
    allSampleInputs.forEach(sampleInput => {
      const qty = parseInt(sampleInput.value) || 0;
      totalSamples += qty;
    });
    
    const limitReached = totalSamples >= maxSampleTotal;

    // Disable/enable plus buttons in cart drawer
    allSampleInputs.forEach(input => {
      const quantityContainer = input.closest('quantity-input');
      if (quantityContainer) {
        const plusButton = quantityContainer.querySelector('.quantity__button[name="plus"]');
        if (plusButton) {
          plusButton.disabled = limitReached;
          plusButton.style.opacity = limitReached ? '0.5' : '1';
          plusButton.style.cursor = limitReached ? 'not-allowed' : 'pointer';
        }
      }
    });

    // Also disable sample selection buttons if they exist
    const sampleButtons = document.querySelectorAll('.sample-btn-target');
    sampleButtons.forEach(button => {
      if (limitReached) {
        button.style.pointerEvents = 'none';
        button.style.opacity = '0.5';
        button.classList.add('samples-disabled');
      } else {
        button.style.pointerEvents = 'auto';
        button.style.opacity = '1';
        button.classList.remove('samples-disabled');
      }
    });
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: document.getElementById('cart-drawer').dataset.id,
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
